import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { NodeSelectionTemplate, UpdateSelectionTemplate } from '@cast/types';

import { getClustersNodeTemplatesQueryKey } from 'hooks/queries/autoscaler/useClustersNodeTemplatesQuery';
import { getSelectionTemplatesQueryKey } from 'hooks/queries/autoscaler/useSelectionTemplatesQuery';
import { getClusterEdgeLocationsQueryKey } from 'hooks/queries/omni/useGetClusterEdgeLocationsQuery';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type Payload = {
  name: string;
  template: UpdateSelectionTemplate;
};

type UseUpdateSelectionTemplateMutationArgs = {
  onSuccess?: (template: NodeSelectionTemplate) => void;
};

export const useUpdateSelectionTemplateMutation = ({
  onSuccess,
}: UseUpdateSelectionTemplateMutationArgs = {}): UseMutationResult<
  AxiosResponse<NodeSelectionTemplate>,
  unknown,
  Payload,
  unknown
> => {
  const { cluster } = useCluster();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, template }) => {
      return apiClient.autoscaler.updateSelectionTemplate({
        clusterId: cluster.id,
        nodeTemplateName: name,
        nodeTemplate: template,
      });
    },
    onSuccess: ({ data: updatedTemplate }) => {
      queryClient.invalidateQueries({
        queryKey: getSelectionTemplatesQueryKey(cluster.id),
      });
      queryClient.invalidateQueries({
        queryKey: getClustersNodeTemplatesQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getClusterEdgeLocationsQueryKey({ clusterId: cluster.id }),
      });
      onSuccess?.(updatedTemplate);
    },
    onError: (error: unknown) => {
      notify.error(parseApiError(error));
    },
  });
};
